import api, { API, dlToken } from "@/lib/api";
import { MODULES, FIBER_COLORS, COLOR_HEX } from "@/lib/modules";
import { useSettings } from "@/context/SettingsContext";
import { useI18n } from "@/context/I18nContext";

const colorForFiber = (n) => COLOR_HEX[FIBER_COLORS[((Number(n) || 1) - 1) % FIBER_COLORS.length]];
const nameForFiber = (n) => FIBER_COLORS[((Number(n) || 1) - 1) % FIBER_COLORS.length];

export function FieldRow({ f, form, set, refs, splitterPorts, initial, isSplice }) {
  const { settings } = useSettings();
  const { t } = useI18n();
  const token = dlToken();

  return (
    <div className={f.type === "textarea" ? "col-span-2" : ""}>
      <label className="text-xs text-slate-400 mb-1 block">
        {t(f.label)}{f.required && <span className="text-red-400"> *</span>}
      </label>
      {f.name === "identyfikator" ? (
        <div data-testid={`field-${f.name}`}
          className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-2 text-sm text-slate-300 flex items-center justify-between gap-2">
          <span className="truncate font-mono">{form[f.name] || "— generowany z SIMC · ULIC · nr budynku"}</span>
          <span className="text-[10px] text-slate-500 shrink-0 uppercase tracking-wide">auto</span>
        </div>
      ) : f.type === "checkboxes" ? (
        <div data-testid={`field-${f.name}`} className="flex flex-wrap gap-2">
          {(f.options || []).map((o) => {
            const arr = Array.isArray(form[f.name]) ? form[f.name] : [];
            const on = arr.includes(o);
            return (
              <label key={o} className={`flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm cursor-pointer border ${on ? "bg-cyan-900/30 border-cyan-600" : "bg-slate-900 border-slate-700 hover:border-slate-600"}`}>
                <input type="checkbox" checked={on} className="accent-cyan-500"
                  data-testid={`field-${f.name}-${o}`}
                  onChange={() => set(f.name, on ? arr.filter((x) => x !== o) : [...arr, o])} />
                {t(o)}
              </label>
            );
          })}
        </div>
      ) : f.type === "radio" ? (
        <div data-testid={`field-${f.name}`} className="flex flex-wrap gap-2">
          {(f.options || []).map((o) => {
            const on = form[f.name] === o;
            return (
              <label key={o} className={`flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm cursor-pointer border ${on ? "bg-cyan-900/30 border-cyan-600" : "bg-slate-900 border-slate-700 hover:border-slate-600"}`}>
                <input type="radio" name={f.name} checked={on} className="accent-cyan-500"
                  data-testid={`field-${f.name}-${o}`}
                  onChange={() => set(f.name, o)} />
                {t(o)}
              </label>
            );
          })}
        </div>
      ) : f.name === "splitter_port" && splitterPorts ? (
        <select value={form[f.name]} onChange={(e) => set(f.name, e.target.value)}
          data-testid={`field-${f.name}`}
          className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500">
          <option value="">—</option>
          {splitterPorts.map((p) => {
            const isCurrent = String(initial?.splitter_port) === String(p.port);
            const disabled = p.occupied && !isCurrent;
            return (
              <option key={p.port} value={p.port} disabled={disabled}>
                Port {p.port}{disabled ? ` — zajęty (${p.client?.name || ""})` : " — wolny"}
              </option>
            );
          })}
        </select>
      ) : f.type === "select" ? (
        <select value={form[f.name]} onChange={(e) => set(f.name, e.target.value)}
          data-testid={`field-${f.name}`}
          className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500">
          <option value="">—</option>
          {(f.optionsKey ? (settings[f.optionsKey] || []) : (f.options || [])).map((o) => { const v = (o && typeof o === "object") ? o.name : o; return <option key={v} value={v}>{t(v)}</option>; })}
        </select>
      ) : f.type === "ref" ? (
        f.readonly ? (
          <div data-testid={`field-${f.name}`}
            className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-2 text-sm text-slate-300 flex items-center justify-between gap-2">
            <span className="truncate">{(() => {
              const sel = (refs[f.ref] || []).find((o) => o.id === form[f.name]);
              return sel ? (sel[MODULES[f.ref].display] || sel.name || sel.id) : "— ustawiany automatycznie po wyborze splittera";
            })()}</span>
            <span className="text-[10px] text-slate-500 shrink-0 uppercase tracking-wide">auto</span>
          </div>
        ) : (
        <>
          <select value={form[f.name]} onChange={(e) => set(f.name, e.target.value)}
            data-testid={`field-${f.name}`}
            className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500">
            <option value="">—</option>
            {(refs[f.ref] || []).map((o) => (
              <option key={o.id} value={o.id}>{o[MODULES[f.ref].display] || o.name || o.id}</option>
            ))}
          </select>
          {MODULES[f.ref]?.catalog && form[f.name] && (() => {
            const sel = (refs[f.ref] || []).find((o) => o.id === form[f.name]);
            return sel?.image_file_id ? (
              <img src={`${API}/files/${sel.image_file_id}?auth=${token}`} alt="podgląd"
                data-testid={`catalog-preview-${f.name}`}
                className="mt-1.5 w-full h-24 object-cover rounded border border-slate-700" />
            ) : null;
          })()}
        </>
        )
      ) : f.type === "textarea" ? (
        <textarea value={form[f.name]} onChange={(e) => set(f.name, e.target.value)} rows={2}
          data-testid={`field-${f.name}`}
          className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500" />
      ) : (
        <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
          value={form[f.name]} onChange={(e) => set(f.name, e.target.value)}
          data-testid={`field-${f.name}`}
          className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500" />
      )}
      {isSplice && (f.name === "fiber_in" || f.name === "fiber_out") && form[f.name] && (
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400" data-testid={`fiber-color-${f.name}`}>
          <span className="w-3 h-3 rounded-full border border-slate-600" style={{ background: colorForFiber(form[f.name]) }} />
          Włókno {form[f.name]} ({nameForFiber(form[f.name])})
        </div>
      )}
    </div>
  );
}
